import {AnchoredOverlay} from '@primer/react'
import React, {useRef} from 'react'

import {AUTO_MODEL_DESCRIPTION, AUTO_MODEL_ID} from '../utils/auto-model'
import type {CopilotChatModel} from '../utils/copilot-chat-types'
import styles from './ModelInfoPopover.module.css'

const FALLBACK_AUTO_DISCOUNT = 0.1

function formatContextWindow(tokens: number): string {
  if (tokens >= 1_000_000) {
    const value = tokens / 1_000_000
    return `${Number(value.toFixed(1).replace(/\.0$/, ''))}M`
  }
  return `${Math.round(tokens / 1000)}k`
}

const TOKENS_PER_MILLION = 1_000_000
const NANO_AIU_DIVISOR = 1_000_000_000

type TokenPrices = NonNullable<NonNullable<CopilotChatModel['billing']>['token_prices']>

type NormalizedTierPrices = {
  inputPrice: number
  outputPrice: number
  cachePrice: number
}

type NormalizedPricing = {
  defaultTier: NormalizedTierPrices
  longContextTier?: NormalizedTierPrices
}

/**
 * Converts raw CAPI token prices into AICs per million tokens.
 * Handles both the legacy flat format (pre-2026-06-01, prices in nano-AIUs)
 * and the tiered format (2026-06-01+, prices in AIUs with default/long_context tiers).
 */
function normalizeTokenPrices(tokenPrices: TokenPrices | undefined): NormalizedPricing | undefined {
  if (!tokenPrices || tokenPrices.batch_size <= 0) return undefined
  const scale = TOKENS_PER_MILLION / tokenPrices.batch_size

  // 2026-06-01+ tiered format: prices already in AIUs, nested under `default`/`long_context`.
  // 2026-07-01+ renames `cache_price` → `cache_read_price` and `context_max` → `max_prompt_tokens`.
  // Prefer new names; fall back to old names for 2026-06-01 responses.
  if (tokenPrices.default) {
    const def = tokenPrices.default
    const result: NormalizedPricing = {
      defaultTier: {
        inputPrice: def.input_price * scale,
        outputPrice: def.output_price * scale,
        cachePrice: (def.cache_read_price ?? def.cache_price ?? 0) * scale,
      },
    }

    // We're not currently surfacing long context pricing in the UI, but normalize it anyway in case we want to use it later
    if (tokenPrices.long_context) {
      const lc = tokenPrices.long_context
      result.longContextTier = {
        inputPrice: lc.input_price * scale,
        outputPrice: lc.output_price * scale,
        cachePrice: (lc.cache_read_price ?? lc.cache_price ?? 0) * scale,
      }
    }
    return result
  }

  // Legacy flat format (pre-2026-06-01): prices in nano-AIUs at the top level
  if (tokenPrices.input_price !== undefined) {
    return {
      defaultTier: {
        inputPrice: (tokenPrices.input_price / NANO_AIU_DIVISOR) * scale,
        outputPrice: ((tokenPrices.output_price ?? 0) / NANO_AIU_DIVISOR) * scale,
        cachePrice: ((tokenPrices.cache_price ?? 0) / NANO_AIU_DIVISOR) * scale,
      },
    }
  }

  return undefined
}

function PricingTierRows({tier}: {tier: NormalizedTierPrices}) {
  return (
    <div className={styles.modelInfoPricingRows}>
      <div className={styles.modelInfoRow}>
        <span>Input:</span>
        <span className={styles.modelInfoRowValue}>{Math.round(tier.inputPrice).toLocaleString()} credits</span>
      </div>
      <div className={styles.modelInfoRow}>
        <span>Cached input:</span>
        <span className={styles.modelInfoRowValue}>{Math.round(tier.cachePrice).toLocaleString()} credits</span>
      </div>
      <div className={styles.modelInfoRow}>
        <span>Output:</span>
        <span className={styles.modelInfoRowValue}>{Math.round(tier.outputPrice).toLocaleString()} credits</span>
      </div>
    </div>
  )
}

function applyDiscountToTier(tier: NormalizedTierPrices, discount: number): NormalizedTierPrices {
  const multiplier = 1 - discount
  return {
    inputPrice: tier.inputPrice * multiplier,
    outputPrice: tier.outputPrice * multiplier,
    cachePrice: tier.cachePrice * multiplier,
  }
}

function applyDiscountToPricing(
  pricing: NormalizedPricing | undefined,
  discount: number,
): NormalizedPricing | undefined {
  if (!pricing) return undefined
  return {
    defaultTier: applyDiscountToTier(pricing.defaultTier, discount),
    longContextTier: pricing.longContextTier ? applyDiscountToTier(pricing.longContextTier, discount) : undefined,
  }
}

function formatDiscount(discount: number): string {
  return `${Number((discount * 100).toFixed(2))}%`
}

function getAutoDiscountNote(discount: number, isPinnedModelDiscount: boolean): string {
  const formattedDiscount = formatDiscount(discount)

  if (isPinnedModelDiscount) {
    return `Use of this model through Auto will be billed at a ${formattedDiscount} discount.`
  }

  return `Model use through Auto will be billed at a ${formattedDiscount} discount.`
}

/**
 * Renders a model's hover popover. One skeleton (title → description → cost → max
 * context → note) serves every entry; only the source of each value and which
 * optional sections appear differ by mode:
 *  - Concrete model: own name/description/context + per-token "Cost / 1M tokens".
 *  - Auto, pre-pin (no resolved model): the Auto entry's own explainer, no context.
 *  - Auto, post-pin: mirrors the picker chip — title "Auto ({model})" plus the
 *    resolved model's description and max context.
 */
function ModelInfoContent({
  model,
  autoPinnedModel,
  autoPinnedModelDisplayName,
  autoPinnedModelDiscount,
}: {
  model: CopilotChatModel
  autoPinnedModel?: CopilotChatModel
  autoPinnedModelDisplayName?: string
  autoPinnedModelDiscount?: number
}) {
  const isAuto = model.id === AUTO_MODEL_ID
  // Post-pin Auto sources its details from the resolved model; pre-pin Auto falls back to
  // the Auto entry's own description but shows no context. Concrete models use their own.
  // The final AUTO_MODEL_DESCRIPTION fallback covers a CAPI-sourced Auto entry that arrives
  // without a description (the generic capabilities fallback has no rule for Auto).
  const contextModel = isAuto ? autoPinnedModel : model
  const autoPinnedModelName = autoPinnedModelDisplayName ?? autoPinnedModel?.displayName
  const title = isAuto ? (autoPinnedModelName ? `Auto (${autoPinnedModelName})` : 'Auto') : model.displayName
  const description = isAuto
    ? (autoPinnedModel?.description ?? model.description ?? AUTO_MODEL_DESCRIPTION)
    : model.description
  const maxContextTokens = contextModel?.capabilities?.limits?.max_context_window_tokens
  const pricing =
    isAuto && autoPinnedModelDiscount !== undefined
      ? applyDiscountToPricing(normalizeTokenPrices(autoPinnedModel?.billing?.token_prices), autoPinnedModelDiscount)
      : !isAuto
        ? normalizeTokenPrices(model.billing?.token_prices)
        : undefined
  const discountNote =
    isAuto && (autoPinnedModelDiscount === undefined || autoPinnedModelDiscount > 0)
      ? getAutoDiscountNote(autoPinnedModelDiscount ?? FALLBACK_AUTO_DISCOUNT, Boolean(autoPinnedModelName))
      : undefined

  return (
    <div id={`model-info-${model.id}`} role="tooltip" className={styles.modelInfoPopover}>
      <p className={styles.modelInfoTitle}>{title}</p>
      {description && <p className={styles.modelInfoDescription}>{description}</p>}
      {pricing && (
        <div className={styles.modelInfoSection}>
          <div className={styles.modelInfoSectionHeader}>
            <strong>Cost / 1M tokens</strong>
          </div>
          <PricingTierRows tier={pricing.defaultTier} />
        </div>
      )}
      {!!maxContextTokens && (
        <div className={styles.modelInfoSection}>
          <div className={styles.modelInfoSectionHeader}>
            <strong>Max context</strong>
          </div>
          <span className={styles.modelInfoMaxContextValue}>{formatContextWindow(maxContextTokens)}</span>
        </div>
      )}
      {discountNote && <p className={styles.modelInfoNote}>{discountNote}</p>}
    </div>
  )
}

export function ModelInfoPopover({
  model,
  autoPinnedModel,
  autoPinnedModelDisplayName,
  autoPinnedModelDiscount,
  anchorRef,
  isOpen,
  onClose,
}: {
  model: CopilotChatModel
  autoPinnedModel?: CopilotChatModel
  autoPinnedModelDisplayName?: string
  autoPinnedModelDiscount?: number
  anchorRef: React.RefObject<HTMLElement | null>
  isOpen: boolean
  onClose: () => void
}) {
  // A null ref prevents Primer's Overlay from returning focus to the anchor
  // element when the popover closes.
  const noReturnFocusRef = useRef<HTMLElement>(null)
  return (
    <AnchoredOverlay
      renderAnchor={null}
      anchorRef={anchorRef}
      open={isOpen}
      onClose={onClose}
      side="outside-left"
      anchorOffset={0}
      align="start"
      displayInViewport
      focusTrapSettings={{disabled: true}}
      focusZoneSettings={{disabled: true}}
      overlayProps={{preventFocusOnOpen: true, returnFocusRef: noReturnFocusRef}}
    >
      <div onMouseLeave={() => onClose()}>
        <ModelInfoContent
          model={model}
          autoPinnedModel={autoPinnedModel}
          autoPinnedModelDisplayName={autoPinnedModelDisplayName}
          autoPinnedModelDiscount={autoPinnedModelDiscount}
        />
      </div>
    </AnchoredOverlay>
  )
}
